import CloseIcon from "@mui/icons-material/Close";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import { Box, Grid, IconButton, Modal, Tooltip, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import BillDetails from "./BillDetails";
import SkeletonLoader from "./SkeletonLoader";
// import { Print } from '../../../../product/price-label/components/Print';
import { cancelLogistics, getOrderDetails, retryRiderEndpoint } from "../../../../config/Services";
import {
  convertToLocalDate,
  convertToLocalTime,
} from "../../product/all-products/Middleware/commonUtils";
import { useSnackbar } from "../../product/hooks/SnackbarProvider";
import BoardItemActions from "./BoardItemActions";
import ValidationModal from "./ValidationModal/ValidationModal";
import { getFromLocalStorage } from "../../../../utils/localStorageHelpers";
import PosCustomIcon from "../../../../customComponents/PosCustomIcon/PosCustomIcon";
import { getStoreCurrencySymbol } from "../../../../utils/currency";
import "./BoardItemDetails.css";
import {
  ArrowTopRightOnSquareIcon,
  BuildingOffice2Icon,
  CubeIcon,
  CurrencyDollarIcon,
  ExclamationCircleIcon,
  MapPinIcon,
  PhoneIcon,
  PrinterIcon,
  PlusIcon,
  Square2StackIcon,
  UserCircleIcon,
  UserIcon,
} from "@heroicons/react/24/outline";
import { XMarkIcon, ArrowPathIcon } from "@heroicons/react/20/solid";
import chefIcon from "../../../../assets/images/chef-hat.png";
import scooterIcon from "../../../../assets/images/scooter.png";
import clockIcon from "../../../../assets/images/outline-clock.png";
import rePrintIconBlack from "../../../../assets/images/re-print-black.png";
import usePrint from "../../product/hooks/usePrint";
import { PosCustomButton } from "../../../../customComponents/PosCustomButton/PosCustomButton";
import TableConfirmationModal from "../../table-layout/components/TableConfirmationModal/TableConfirmationModal";

const BoardItemDetails = ({
  item,
  handleClose,
  validationModal,
  setValidationModal,
  moveItemToState,
  startPrint,
  riderCancelled,
  setRiderCancelled,
  onCancelOrder,
  cancelLoading,
}) => {
  const showSnackbar = useSnackbar();
  const { printToken } = usePrint();

  const [isTokenPrinting, setIsTokenPrinting] = useState(false);
  const [isInvoicePrinting, setIsInvoicePrinting] = useState(false);
  const [orderDetails, setOrderDetails] = useState({});
  const [shipmentCancelLoader, setShipmentCancelLoader] = useState(false);
  const [tableConfirmationModalOpen, setTableConfirmationModalOpen] = useState(false);
  const [retryLogisticsLoader, setRetryLogisticsLoader] = useState(false);
  const locId = getFromLocalStorage("locId");
  const retailType = localStorage.getItem("retailType");
  const retailUserDetails = JSON.parse(localStorage.getItem("retailUserDetails")) || {};

  const openModal = () => setValidationModal(true);

  const closeModal = () => setValidationModal(false);

  const handleReprintInvoice = () => {
    if (isInvoicePrinting) return;

    setIsInvoicePrinting(true);

    startPrint({
      orderId: item?.orderId,
      printType: "invoice",
      skipBill: false,
      skipKot: true,
      ...(orderDetails?.baseOrderResponse?.subOrderType === "DELIVERY" && {
        orderSource: "b2c",
      }),
      onPrintComplete: () => {
        showSnackbar("Invoice printed successfully", "success");
        setIsInvoicePrinting(false);
      },
      onPrintError: () => {
        showSnackbar("Invoice printing failed", "warning");
        setIsInvoicePrinting(false);
      },
    });
  };

  const handleReprintKot = async () => {
    if (isTokenPrinting) return;

    setIsTokenPrinting(true);

    try {
      await printToken({
        tableId: orderDetails?.baseOrderResponse?.tableId || null,
        tokenNumber: orderDetails?.orderTokenDetails?.tokenNumber,
        tableNumber: orderDetails?.baseOrderResponse?.tableNumber,
        subOrderType: orderDetails?.baseOrderResponse?.subOrderType,
        customerName: orderDetails?.baseOrderResponse?.customerName || "Walk-in Customer",
        orderItems: orderDetails?.baseOrderResponse?.orderItemList,
        comments: orderDetails?.baseOrderResponse?.comments || "N/A",
        layoutName: orderDetails?.baseOrderResponse?.layoutName || null,
        startTime: orderDetails?.baseOrderResponse?.startTime,
        endTime: orderDetails?.baseOrderResponse?.endTime,
        slotId: item?.slotId,
      });

      showSnackbar("Token printed successfully", "success");
    } catch (error) {
      showSnackbar("Token printing failed", "warning");
    } finally {
      setIsTokenPrinting(false);
    }
  };

  const orderQuery = useQuery(
    ["order", validationModal],
    () => getOrderDetails(item?.orderId, locId),
    {
      retry: 2,
      refetchOnWindowFocus: false,
      onSuccess: (res) => {
        setOrderDetails(res?.data?.data);
      },
      onError: (error) => {
        showSnackbar(error?.response?.data?.message || error?.message, "error");
        handleClose();
      },
    }
  );

  const formatSalesChannel = (channel) => {
    if (!channel) return "";

    const channelMap = {
      SWIGGY_ORDER: {
        label: "Swiggy",
        color: "#F6881F",
        backgroundColor: "#FFF9F3",
      },
      ZOMATO_ORDER: {
        label: "Zomato",
        color: "#E23744",
        backgroundColor: "#FFEBED",
      },
      UBER_EATS: {
        label: "Dine In",
        color: "#4CAF50",
        backgroundColor: "#FFF9F3",
      },
      RESTAURANT_ORDER: {
        label: "Restaurant Order",
        color: "#FF9800",
        backgroundColor: "#FFF9F3",
      },
      DELIVERY: {
        label: "Delivery",
        color: "#2196F3",
        backgroundColor: "#FFF9F3",
      },
      POS: {
        label: "POS",
        color: "#9C27B0",
        backgroundColor: "#FFF9F3",
      },
      ONLINE: {
        label: "Online",
        color: "#00BCD4",
        backgroundColor: "#FFF9F3",
      },
      WHATSAPP_ORDER: {
        label: "Whatsapp",
        color: "#4CAF50",
        backgroundColor: "#FFF9F3",
      },
      B2C_ORDER: {
        label: "Website Order",
        color: "#FF9800",
        backgroundColor: "#FFF9F3",
      },
    };

    if (channelMap[channel]) {
      return channelMap[channel];
    }

    const formatted = channel.replace(/_/g, " ").toLowerCase();
    return {
      label: formatted.charAt(0).toUpperCase() + formatted.slice(1),
      color: "#757575",
    };
  };

  const salesChannelInfo = formatSalesChannel(item?.salesChannel);

  const timeConverter = (minutes) => {
    if (!minutes) return 0;
    if (minutes < 60) return `${minutes}mins`;
    const hrs = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hrs}hrs ${mins}mins`;
  };

  const getCardInfos = (orderDetails) => {
    const infoMap = [
      {
        label: "PREP TIME",
        secondaryText: timeConverter(orderDetails?.baseOrderResponse?.aggregatorPrepTime) || "N/A",
        logo: chefIcon,
      },
      {
        label: "DELIVERY TYPE",
        secondaryText: item?.riderChannel ? `Partner: ${item?.riderChannel}` : "Partner: N/A",
        logo: scooterIcon,
      },
      {
        label: "RIDER ETA",
        secondaryText: timeConverter(item?.expectedPickupTime) || "N/A",
        logo: clockIcon,
      },
    ];

    return infoMap;
  };

  const cardsInfo = getCardInfos(orderDetails);

  const renderDeliverySlot = (order) => {
    if (!order?.startTime || !order?.endTime)
      return (
        <>
          <div>Delivery Slot: </div>
          <div>N/A</div>
        </>
      );
    return (
      <div className="board-item-details-delivery-slot">
        <div>
          {`Delivery Slot: ${convertToLocalTime(order?.startTime)} - ${convertToLocalTime(
            order?.endTime
          )}`}
        </div>
        <div className="board-item-details-delivery-slot-date">
          ({convertToLocalDate(order?.startTime)})
        </div>
      </div>
    );
  };

  const getRiderStatus = (status) => {
    if (!status) {
      return {
        label: "Rider Not Assigned",
        color: "#87125A",
        legend: "#87125A",
        backgroundColor: "#FFDDF2",
      };
    }

    const statusMap = {
      SCHEDULED: {
        label: "Assignment Scheduled",
        color: "#0860e6",
        legend: "#0860e6",
        backgroundColor: "#edf8ff",
      },

      ASSIGNMENT_IN_PROGRESS: {
        label: "Searching For Rider",
        color: "#414651",
        legend: "#414651",
        backgroundColor: "#FFFEE7",
      },

      CANCELLED: {
        label: "Logistics Canceled",
        color: "#E90000",
        legend: "#E90000",
        backgroundColor: "#FFC8C5",
      },

      ASSIGNED: {
        label: "Rider Assigned",
        color: "#0860e6",
        legend: "#0860e6",
        backgroundColor: "#edf8ff",
      },

      UNASSIGNED: {
        label: "Rider Not Assigned",
        color: "#87125A",
        legend: "#87125A",
        backgroundColor: "#FFDDF2",
      },

      REASSIGNED: {
        label: "Rider Changed",
        color: "#414651",
        legend: "#414651",
        backgroundColor: "#FFF5B2",
      },

      AT_STORE: {
        label: "Rider Arrived",
        color: "#BA623C",
        legend: "#BA623C",
        backgroundColor: "#F6EBDE",
      },

      IN_TRANSIT: {
        label: "Out For Delivery",
        color: "#414651",
        legend: "#414651",
        backgroundColor: "#E9EAEB",
      },

      DELIVERED: {
        label: "Delivered",
        color: "#095120",
        legend: "#095120",
        backgroundColor: "#BFE6A3",
      },

      ADDITIONAL_RIDER_ASSIGNED: {
        label: "Additional Rider Assigned",
        color: "#0860e6",
        legend: "#0860e6",
        backgroundColor: "#edf8ff",
      },
    };

    if (statusMap[status]) {
      return statusMap[status];
    }
  };

  // const riderStatusInfo = getRiderStatus(item?.riderCurrentStatus);

  const riderStatusInfo = getRiderStatus(
    orderDetails?.baseOrderResponse?.riderCurrentStatus ?? item?.riderCurrentStatus
  );

  const formatUTCToLocalDateTime = (utcString) => {
    if (!utcString) return "";

    const date = new Date(utcString);

    const datePart = date.toLocaleDateString("en-CA"); // YYYY-MM-DD
    const timePart = date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });

    return `${datePart}, ${timePart}`;
  };

  const getDeliveryAddress = () => {
    if (!orderDetails?.addressEntityModel?.billingAddress?.addressLine1) {
      return "N/A";
    }

    const addressLineOne = orderDetails?.addressEntityModel?.billingAddress?.addressLine1;
    const addressLineTwo = orderDetails?.addressEntityModel?.billingAddress?.addressLine2;
    const city = orderDetails?.addressEntityModel?.billingAddress?.city;
    const pinCode = orderDetails?.addressEntityModel?.billingAddress?.pinCode;
    const state = orderDetails?.addressEntityModel?.billingAddress?.state;
    const country = orderDetails?.addressEntityModel?.billingAddress?.country;

    return `${addressLineOne}, ${addressLineTwo ? `${addressLineTwo},` : ""} ${
      city ? `${city},` : ""
    } ${pinCode ? `${pinCode},` : ""} ${state ? `${state},` : ""} ${country ? country : ""}`;
  };

  const gettingPaymentStatus = (status) => {
    if (status === "COMPLETED") {
      return "PAID";
    } else {
      return "NOT PAID";
    }
  };

  const getPaymentLegend = (status) => {
    const statusMap = {
      COMPLETED: {
        color: "#095120",
        backgroundColor: "#BFE6A3",
      },
    };
    return (
      statusMap[status] || {
        color: "#E23744",
        backgroundColor: "#FFEBED",
      }
    );
  };

  const paymentLegend = getPaymentLegend(item?.paymentStatus);

  const handleLogisticCancel = () => {
    const fullName = `${retailUserDetails?.firstName} ${retailUserDetails?.secondName}`;
    setShipmentCancelLoader(true);
    const payload = {
      orderId: item?.orderId,
      updatedBy: retailUserDetails?.uidx,
      updatedByName: fullName,
      reason: "Canceled from ODS",
    };
    cancelLogistics(payload)
      .then(async (res) => {
        if (res?.data?.data.es > 0) {
          showSnackbar(res?.data?.data?.message || "Unable to cancel shipment", "error");
          setShipmentCancelLoader(false);
          return;
        }
        showSnackbar("Shimpment Canceled Successfully", "success");

        const refetchResult = await orderQuery.refetch();
        const freshOrderData = refetchResult?.data?.data?.data;
        if (freshOrderData) {
          setOrderDetails(freshOrderData);
        }

        setShipmentCancelLoader(false);
        // setRiderCancelled(true);
      })
      .catch((err) => {
        showSnackbar(err?.message || "Unable to cancel shipment", "error");
        setShipmentCancelLoader(false);
        // setRiderCancelled(false);
      });
  };

  const handleComfirmationModalOpen = () => {
    setTableConfirmationModalOpen(true);
  };

  const riderStatus =
    orderDetails?.baseOrderResponse?.riderCurrentStatus ?? item?.riderCurrentStatus;

  const isRiderNotAssigned =
    !riderStatus ||
    ["SCHEDULED", "ASSIGNMENT_IN_PROGRESS", "UNASSIGNED", "CREATED", "PENDING"].includes(
      riderStatus
    );

  const isShowCancelButton =
    !isRiderNotAssigned &&
    riderStatus !== "CANCELLED" &&
    item?.fulfilmentStatus !== "CREATED" &&
    item?.fulfilmentStatus !== "IN_TRANSIT" &&
    item?.fulfilmentStatus !== "DELIVERED";

  const handleReAssignRider = async () => {
    try {
      setRetryLogisticsLoader(true);
      const retryRiderResponse = await retryRiderEndpoint(item?.orderId);

      if (
        retryRiderResponse?.data?.data?.statusCode === 200 ||
        retryRiderResponse?.data?.data?.statusCode === 201
      ) {
        // refetching the orders endpoint
        const refetchResult = await orderQuery.refetch();
        const freshBaseOrderResponse = refetchResult?.data?.data?.data?.baseOrderResponse;

        if (freshBaseOrderResponse?.alert === false) {
          // setTimeout(() => {
          showSnackbar(freshBaseOrderResponse?.alertMessage, "warning");
          // }, 2500);

          // setRiderCancelled(true);
        } else {
          showSnackbar("Retry logistics successfull", "success");
        }
      }

      setRetryLogisticsLoader(false);
    } catch (err) {
      showSnackbar("Failed to retry logistics", "error");
      // setRetryLogisticsLoader(false);
    }
  };

  const handleLogisticRetry = () => {
    showSnackbar("Retry logisctics feature is comming soon", "warning");
    return;
  };

  const formatTime = (time, format) => {
    if (!time) return "";

    // Treat input as UTC
    const date = new Date(time + "Z");

    return date.toLocaleTimeString("en-IN", {
      timeZone: "Asia/Kolkata",
      hour: "2-digit",
      minute: "2-digit",
      hour12: format !== "24h",
    });
  };

  const slotStartTime = formatTime(item?.startTime, "12h");
  const slotEndTime = formatTime(item?.endTime, "12h");

  return (
    <>
      <div className="baord-details-header-container">
        {validationModal ? null : (
          <div className="board-details-header">
            <div className="board-details-orderId">
              <p className="board-details-orderId-label">Order ID:</p>
              <span className="board-details-id">
                {item?.externalOrderId || item?.orderId || "N/A"}
              </span>
              <div className="board-details-status">
                <p>{item?.fulfilmentStatus}</p>
              </div>
              <div className="board-details-slot">
                {item?.slotId?.startsWith("IN") ? (
                  <p>Instant Delivery</p>
                ) : (
                  <>
                    <p>
                      {slotStartTime} - {slotEndTime}
                    </p>
                  </>
                )}
              </div>
            </div>
            <div className="board-details-close-btn">
              <PosCustomIcon
                icon={<XMarkIcon />}
                height={"16px"}
                width={"16px"}
                pointer={true}
                onClickFunction={handleClose}
              />
            </div>
          </div>
        )}
      </div>
      {orderQuery?.isLoading || orderQuery?.isFetching ? (
        <SkeletonLoader type="order-details" />
      ) : (
        <Box
          className={`board-item-details-content ${
            retailType === "GROCERY" &&
            item?.fulfilmentStatus === "CREATED" &&
            "item-details-content-padding-bottom "
          }`}
        >
          {!validationModal ? (
            <>
              {/* Special Instructions Comments Section */}
              {orderDetails?.baseOrderResponse?.orderInstructions && (
                <div className="board-item-details-instruction">
                  <div className="board-item-details-instruction-heading">
                    <PosCustomIcon
                      icon={<ExclamationCircleIcon />}
                      height={"16px"}
                      width={"16px"}
                      color="var(--surface-g5)"
                    />
                    <span>Order Comments</span>
                  </div>
                  <p className="board-details-instruction-value">
                    {orderDetails?.baseOrderResponse?.orderInstructions}
                  </p>
                </div>
              )}

              {/* 5 Card Header Grid */}
              <div className="board-details-grid-cards">
                {/* Card 1: Source */}
                <div className="board-details-card">
                  <p className="board-details-card-label">Source</p>
                  <div
                    className="board-details-card-badge"
                    style={{
                      color: salesChannelInfo?.color || "#10b981",
                      backgroundColor: salesChannelInfo?.backgroundColor || "#ecfdf5",
                      borderColor: salesChannelInfo?.color || "#a7f3d0",
                    }}
                  >
                    {salesChannelInfo?.label || "N/A"}
                  </div>
                </div>

                {/* Card 2: Created At */}
                <div className="board-details-card">
                  <p className="board-details-card-label">Created At</p>
                  <p className="board-details-card-value">
                    {formatUTCToLocalDateTime(item?.createdAt)}
                  </p>
                </div>

                {/* Cards 3, 4, 5 from cardsInfo */}
                {cardsInfo &&
                  cardsInfo?.length > 0 &&
                  cardsInfo?.map((card, idx) => (
                    <div key={idx} className="board-details-card">
                      <p className="board-details-card-label">{card?.label}</p>
                      <div className="board-details-card-value-with-icon">
                        <PosCustomIcon image={card?.logo} height={"16px"} width={"16px"} />
                        <p className="board-details-card-value">{card?.secondaryText}</p>
                      </div>
                    </div>
                  ))}
              </div>

              {/* Grid with 2 columns */}
              <div className="board-details-columns-grid">
                {/* Left Column (Payment & Items) */}
                <div className="board-details-column-left">
                  {/* Payment Card */}
                  <div className="board-payment-card">
                    <div className="board-payment-grid">
                      <div>
                        <p className="board-payment-label">Payment Status</p>
                        <div
                          className="board-payment-badge"
                          style={{
                            backgroundColor: paymentLegend?.backgroundColor,
                            borderColor: paymentLegend?.color,
                            color: paymentLegend?.color,
                          }}
                        >
                          <div
                            className="board-payment-dot"
                            style={{ backgroundColor: paymentLegend?.color }}
                          ></div>
                          <span>{gettingPaymentStatus(item?.paymentStatus)}</span>
                        </div>
                      </div>
                      <div>
                        <p className="board-payment-label">Payment Method</p>
                        <p className="board-payment-method-value">{item?.paymentMethod || "N/A"}</p>
                      </div>
                    </div>
                  </div>

                  {/* Order Items Card */}
                  <div className="board-items-card">
                    <div className="board-items-header">
                      <h3 className="board-items-title">Order Items</h3>
                      <span className="board-items-count">
                        {orderDetails?.baseOrderResponse?.orderItemList?.length || 0} items
                      </span>
                    </div>
                    <div className="board-items-list-container">
                      <div className="board-items-list">
                        {orderDetails?.baseOrderResponse?.orderItemList?.map((orderItem, idx) => (
                          <div key={idx} className="board-item-row">
                            <div className="board-item-row-content">
                              <div className="board-item-info">
                                <div className="board-item-name-price">
                                  <p className="board-item-name">
                                    {orderItem?.productName}
                                    {orderItem?.variantName &&
                                      orderItem?.variantName !== "Regular" &&
                                      ` (${orderItem?.variantName})`}
                                  </p>
                                  <p className="board-item-price">
                                    {getStoreCurrencySymbol()}
                                    {retailType === "RESTAURANT"
                                      ? orderItem?.subTotal
                                      : orderItem?.mrpTotal}
                                  </p>
                                </div>
                                <p className="board-item-qty">QTY: {orderItem?.quantity}</p>

                                {/* Addons */}
                                {orderItem?.addonProduct && orderItem?.addonProduct?.length > 0 && (
                                  <div className="board-item-addons">
                                    {orderItem?.addonProduct?.map((addon, aIdx) => (
                                      <span key={aIdx} className="board-item-addon-tag">
                                        + {addon?.itemName}&nbsp;(x{addon?.quantity})
                                      </span>
                                    ))}
                                  </div>
                                )}

                                {/* Special Comments */}
                                {orderItem?.comments && (
                                  <div className="board-item-comment-box">
                                    <span className="board-item-comment-tag">
                                      <PosCustomIcon
                                        icon={<ExclamationCircleIcon />}
                                        height={"14px"}
                                        width={"14px"}
                                        color="#b45309"
                                      />
                                      <span className="board-item-comment-text">
                                        {orderItem?.comments}
                                      </span>
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right Column (Rider, Customer, Price) */}
                <div className="board-details-column-right">
                  {/* Rider Information Card */}
                  <div className="board-rider-card">
                    <div className="board-rider-header">
                      <h3 className="board-rider-title">Rider Information</h3>
                      {isRiderNotAssigned ? (
                        <div className="board-rider-status-tag-unassigned">Unassigned</div>
                      ) : (
                        <div
                          className="board-rider-status-tag"
                          style={{
                            backgroundColor: riderStatusInfo?.backgroundColor,
                            borderColor: riderStatusInfo?.color,
                            color: riderStatusInfo?.color,
                          }}
                        >
                          {riderStatusInfo?.label}
                        </div>
                      )}
                    </div>

                    {isRiderNotAssigned ? (
                      <div className="board-rider-unassigned-body">
                        <p className="board-rider-unassigned-text">No delivery partner assigned yet.</p>
                        <button className="board-rider-add-partner-btn" onClick={handleReAssignRider}>
                          <div className="board-rider-add-partner-left">
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2" />
                              <path d="M15 18H9" />
                              <path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14" />
                              <circle cx="17" cy="18" r="2" />
                              <circle cx="7" cy="18" r="2" />
                            </svg>
                            Add Logistics Partner
                          </div>
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="m9 18 6-6-6-6" />
                          </svg>
                        </button>
                      </div>
                    ) : (
                      <>
                        {isShowCancelButton && (
                          <div className="board-rider-cancel-btn">
                            <PosCustomButton
                              title={
                                orderDetails?.baseOrderResponse?.riderCurrentStatus === "CANCELLED"
                                  ? "Cancelled"
                                  : "Cancel Assignment"
                              }
                              variant={
                                orderDetails?.baseOrderResponse?.riderCurrentStatus === "CANCELLED"
                                  ? "blue-s-transparent"
                                  : "red-transparent"
                              }
                              p="6px 10px"
                              radius="6px"
                              onClickFunction={handleComfirmationModalOpen}
                              loading={shipmentCancelLoader}
                              disabled={
                                orderDetails?.baseOrderResponse?.riderCurrentStatus === "CANCELLED"
                              }
                            />
                          </div>
                        )}
                        <div className="board-rider-body">
                          <div className="board-rider-fields-grid">
                            <div className="board-rider-field-box">
                              <p className="board-rider-field-label">Name</p>
                              <p className="board-rider-field-value">{item?.riderName || "N/A"}</p>
                            </div>
                            <div className="board-rider-field-box">
                              <p className="board-rider-field-label">Contact</p>
                              <p className="board-rider-field-value">{item?.riderPhone || "N/A"}</p>
                            </div>
                          </div>
                          <div className="board-rider-otp-box">
                            <div className="board-rider-otp-content">
                              <span className="board-rider-otp-label">Pickup OTP</span>
                              <span className="board-rider-otp-value">
                                {item?.riderOtp || "N / A"}
                              </span>
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Customer Information Card */}
                  <div className="board-customer-card">
                    <h3 className="board-customer-title">Customer Information</h3>
                    <div className="board-customer-body">
                      {/* Name & Contact Row */}
                      <div className="board-customer-grid-name-contact">
                        <div className="board-customer-field-box">
                          <div className="board-customer-field-header">
                            <p className="board-customer-field-label">Name</p>
                            <Tooltip title={"Copy Customer Name"} placement="top">
                              <span
                                className="board-customer-copy-btn"
                                onClick={() => {
                                  if (item?.customerName) {
                                    navigator.clipboard.writeText(item?.customerName);
                                    showSnackbar("Customer Name copied successfully", "success");
                                  }
                                }}
                              >
                                <PosCustomIcon
                                  icon={<Square2StackIcon />}
                                  width={"13px"}
                                  height={"13px"}
                                  color="#94a3b8"
                                />
                              </span>
                            </Tooltip>
                          </div>
                          <p className="board-customer-field-value">
                            {item?.customerName || "N/A"}
                          </p>
                        </div>

                        <div className="board-customer-field-box">
                          <div className="board-customer-field-header">
                            <p className="board-customer-field-label">Contact</p>
                            <Tooltip title={"Copy Customer Contact"} placement="top">
                              <span
                                className="board-customer-copy-btn"
                                onClick={() => {
                                  if (item?.mobileNumber) {
                                    navigator.clipboard.writeText(item?.mobileNumber);
                                    showSnackbar("Mobile Number copied successfully", "success");
                                  }
                                }}
                              >
                                <PosCustomIcon
                                  icon={<Square2StackIcon />}
                                  width={"13px"}
                                  height={"13px"}
                                  color="#94a3b8"
                                />
                              </span>
                            </Tooltip>
                          </div>
                          <p className="board-customer-field-value">
                            {item?.mobileNumber || "N/A"}
                          </p>
                        </div>
                      </div>

                      {/* Address Row */}
                      <div className="board-customer-field-box">
                        <div className="board-customer-field-header">
                          <p className="board-customer-field-label">Address</p>
                          <Tooltip title={"Copy Customer Address"} placement="top">
                            <span
                              className="board-customer-copy-btn"
                              onClick={() => {
                                if (getDeliveryAddress()) {
                                  navigator.clipboard.writeText(getDeliveryAddress());
                                  showSnackbar("Address copied successfully", "success");
                                }
                              }}
                            >
                              <PosCustomIcon
                                icon={<Square2StackIcon />}
                                width={"13px"}
                                height={"13px"}
                                color="#94a3b8"
                              />
                            </span>
                          </Tooltip>
                        </div>
                        <p className="board-customer-field-value">{getDeliveryAddress()}</p>
                      </div>

                      {/* Geo & Map Link Row */}
                      <div className="board-customer-grid-geo">
                        <div className="board-customer-field-box">
                          <div className="board-customer-field-header">
                            <p className="board-customer-field-label">Geo Location</p>
                            {orderDetails?.addressEntityModel?.shippingAddress?.latitude && (
                              <Tooltip title={"Copy Geo Location"} placement="top">
                                <span
                                  className="board-customer-copy-btn"
                                  onClick={() => {
                                    if (
                                      orderDetails?.addressEntityModel?.shippingAddress?.latitude &&
                                      orderDetails?.addressEntityModel?.shippingAddress?.longitude
                                    ) {
                                      navigator.clipboard.writeText(
                                        `${orderDetails?.addressEntityModel?.shippingAddress?.latitude}, ${orderDetails?.addressEntityModel?.shippingAddress?.longitude}`
                                      );
                                      showSnackbar("Geo Location copied successfully", "success");
                                    }
                                  }}
                                >
                                  <PosCustomIcon
                                    icon={<Square2StackIcon />}
                                    width={"13px"}
                                    height={"13px"}
                                    color="#94a3b8"
                                  />
                                </span>
                              </Tooltip>
                            )}
                          </div>
                          <p className="board-customer-field-value">
                            {`${
                              orderDetails?.addressEntityModel?.shippingAddress?.latitude
                                ? `${orderDetails.addressEntityModel.shippingAddress.latitude}°`
                                : "NA"
                            }, ${
                              orderDetails?.addressEntityModel?.shippingAddress?.longitude
                                ? `${orderDetails.addressEntityModel.shippingAddress.longitude}°`
                                : "NA"
                            }`}
                          </p>
                        </div>

                        <div className="board-customer-field-box">
                          <div className="board-customer-field-header">
                            <p className="board-customer-field-label">Map Link</p>
                            <Tooltip title={"Copy Map Location"} placement="top">
                              <span
                                className="board-customer-copy-btn"
                                onClick={() => {
                                  if (
                                    orderDetails?.addressEntityModel?.shippingAddress?.latitude &&
                                    orderDetails?.addressEntityModel?.shippingAddress?.longitude
                                  ) {
                                    navigator.clipboard.writeText(
                                      `https://google.com/maps?q=${orderDetails?.addressEntityModel?.shippingAddress?.latitude},${orderDetails?.addressEntityModel?.shippingAddress?.longitude}`
                                    );
                                    showSnackbar("Map Location copied successfully", "success");
                                  }
                                }}
                              >
                                <PosCustomIcon
                                  icon={<Square2StackIcon />}
                                  width={"13px"}
                                  height={"13px"}
                                  color="#94a3b8"
                                />
                              </span>
                            </Tooltip>
                          </div>
                          <p className="board-customer-field-value">
                            <a
                              href={`https://www.google.com/maps?q=${orderDetails?.addressEntityModel?.shippingAddress?.latitude},${orderDetails?.addressEntityModel?.shippingAddress?.longitude}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="board-customer-map-link"
                            >
                              Open Link
                              <PosCustomIcon
                                icon={<ArrowTopRightOnSquareIcon />}
                                height={"13px"}
                                width={"13px"}
                                color="#2A71FF"
                              />
                            </a>
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Pricing Details Card */}
                  <div className="board-pricing-card">
                    <h3 className="board-pricing-title">Pricing Details</h3>
                    <div className="board-pricing-body">
                      <div className="board-pricing-rows">
                        <div className="board-pricing-row">
                          <span>Subtotal</span>
                          <span>{getStoreCurrencySymbol()}{orderDetails?.orderBillingDetails?.subTotal || 0}</span>
                        </div>
                        <div className="board-pricing-row">
                          <span>Tax</span>
                          <span>{getStoreCurrencySymbol()}{orderDetails?.orderBillingDetails?.tax || 0}</span>
                        </div>
                        <div className="board-pricing-row">
                          <span>Charges</span>
                          <span>{getStoreCurrencySymbol()}{orderDetails?.baseOrderResponse?.totalCharges || 0}</span>
                        </div>
                        <div className="board-pricing-row">
                          <span>Total Discount</span>
                          <span>-{getStoreCurrencySymbol()}{orderDetails?.orderBillingDetails?.totalDiscount || 0}</span>
                        </div>
                      </div>
                      <div className="board-pricing-divider"></div>
                      <div className="board-pricing-total-row">
                        <span>Total Payable</span>
                        <span>{getStoreCurrencySymbol()}{orderDetails?.orderBillingDetails?.grandTotal || 0}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <Box className="single-page-open-box">
              <ValidationModal
                orderId={item?.orderId}
                orderDetails={orderDetails}
                closeModal={closeModal}
                validationModal={validationModal}
                moveItemToState={moveItemToState}
                startPrint={startPrint}
              />
            </Box>
          )}
          <div className="board-item-details-right-box">
            <div className="board-item-details-actions">
              <BoardItemActions
                item={orderDetails?.baseOrderResponse}
                orderQuery={orderQuery}
                openModal={openModal}
                handleClose={handleClose}
                orderId={item?.orderId}
                validationModal={validationModal}
              />
            </div>
          </div>

          {retailType === "RESTAURANT" && (
            <div className="board-tiem-details-reprint-buttons">
              {onCancelOrder && item?.fulfilmentStatus === "CREATED" && (
                <button
                  className="board-tiem-details-reject-btn"
                  style={{ flex: 1 }}
                  disabled={cancelLoading}
                  onClick={() => {
                    onCancelOrder(item?.orderId);
                  }}
                >
                  Reject Order
                  {cancelLoading && (
                    <PosCustomIcon
                      icon={<ArrowPathIcon className="spinner" />}
                      height={"16px"}
                      width={"16px"}
                      color="#E90000"
                    />
                  )}
                </button>
              )}

              <button
                className="board-tiem-details-reprint-btn"
                style={{ flex: 1 }}
                disabled={isInvoicePrinting}
                onClick={handleReprintInvoice}
              >
                Reprint Invoice
                {isInvoicePrinting ? (
                  <PosCustomIcon
                    icon={<ArrowPathIcon className="spinner" />}
                    height={"16px"}
                    width={"16px"}
                    color="#334155"
                  />
                ) : (
                  <PosCustomIcon
                    icon={<PrinterIcon />}
                    height={"16px"}
                    width={"16px"}
                    color="#334155"
                  />
                )}
              </button>

              <button
                className="board-tiem-details-reprint-btn"
                style={{ flex: 1 }}
                disabled={isTokenPrinting}
                onClick={handleReprintKot}
              >
                Reprint KOT
                {isTokenPrinting ? (
                  <PosCustomIcon
                    icon={<ArrowPathIcon className="spinner" />}
                    height={"16px"}
                    width={"16px"}
                    color="#334155"
                  />
                ) : (
                  <PosCustomIcon image={rePrintIconBlack} height={"16px"} width={"16px"} />
                )}
              </button>
            </div>
          )}

          <TableConfirmationModal
            tableConfirmationModalOpen={tableConfirmationModalOpen}
            setTableConfirmationModalOpen={setTableConfirmationModalOpen}
            text={"Do you want to cancel the shimpment"}
            confirmationButtonText={"Yes cancel shipment"}
            onConfirm={() => {
              handleLogisticCancel();
              setTableConfirmationModalOpen(false);
            }}
          />
        </Box>
      )}
    </>
  );
};

export default BoardItemDetails;
